import { patchAction, type Action, type ActionPatch, type Hook, type Flag, type Perk } from './actions';
import type { Modifier } from './stats';
import type { Tag } from './tags';
import type { HeroId } from './heroes';

export type SkillKind = 'active' | 'auto' | 'trait';

/**
 * A node of a skill's evolution tree. Ring one hangs off the skill, ring two off a ring-one node.
 * `action` replaces what the node inherits, `patch` tweaks it; mods/hooks/flags stack.
 */
export interface AspectDef {
  id: string;
  /** replaces the inherited action */
  action?: Action;
  /** shallow overrides on the inherited action */
  patch?: ActionPatch;
  mods?: Modifier[];
  hooks?: Hook[];
  flags?: Flag[];
  cooldownMul?: number;
  color?: string;
  /** ring two */
  children?: AspectDef[];
}

export interface SkillDef {
  id: string;
  kind: SkillKind;
  tags: Tag[];
  color: string;
  glyph: string;
  /** seconds; for autos the period */
  cooldown?: number;
  action?: Action;
  aspects?: AspectDef[];
  /** per-level scaling; damage is additive percent per level beyond 1 */
  perLevel?: { damage?: number; count?: number; radius?: number; cooldown?: number; duration?: number };
  maxLevel: number;
  /** trait payload */
  mods?: Modifier[];
  /** modifiers added per level beyond 1 (traits) */
  modsPerLevel?: Modifier[];
  hooks?: Hook[];
  flags?: Flag[];
  heroWeight?: Partial<Record<HeroId, number>>;
  /** appears more often when the build already has these tags */
  synergy?: Tag[];
}

const P = (a: Partial<import('./actions').ProjectileAction> & { visual: string; tags: Tag[] }): Action => ({
  type: 'projectile',
  count: 1,
  spread: 0,
  pattern: 'aimed',
  speed: 520,
  range: 620,
  damage: 1,
  radius: 8,
  ...a,
});

export const SKILLS: Record<string, SkillDef> = {
  // ---------------------------------------------------------------- ACTIVE
  wail: {
    id: 'wail', kind: 'active', tags: ['summon', 'void'], color: '#8a7cff', glyph: 'W', cooldown: 14, maxLevel: 5,
    action: { type: 'summon', unit: 'shade', count: 2, duration: 14, at: 'self' },
    perLevel: { damage: 0.2, count: 0.5, duration: 1 },
    heroWeight: { revenant: 3, mystic: 1.5 },
    aspects: [
      { id: 'wail_legion', action: { type: 'summon', unit: 'shade_lesser', count: 5, duration: 12, at: 'self' }, color: '#6f5fd8' },
      { id: 'wail_champion', action: { type: 'summon', unit: 'shade_champion', count: 1, duration: 18, at: 'self' }, color: '#b3a6ff' },
    ],
  },
  light_volley: {
    id: 'light_volley', kind: 'active', tags: ['holy', 'pierce', 'heal', 'projectile'], color: '#ffe38a', glyph: 'V', cooldown: 7, maxLevel: 5,
    action: P({ count: 5, spread: 0.7, pierce: 2, damage: 0.9, speed: 640, range: 700, tags: ['holy', 'pierce', 'projectile'], visual: 'arrow_light' }),
    perLevel: { damage: 0.2, count: 0.5 },
    heroWeight: { pilgrim: 3, bard: 1.5 },
    aspects: [
      { id: 'volley_lance', action: P({ count: 1, pierce: 99, damage: 3.2, radius: 14, speed: 900, range: 900, tags: ['holy', 'pierce', 'projectile'], visual: 'lance_light' }), color: '#fff2b3' },
      { id: 'volley_rain', action: P({ count: 12, spread: 6.283, pierce: 1, damage: 0.7, pattern: 'ring', speed: 560, range: 520, tags: ['holy', 'pierce', 'projectile'], visual: 'arrow_light' }), color: '#ffd45c' },
    ],
  },
  hex: {
    id: 'hex', kind: 'active', tags: ['curse', 'area'], color: '#c65cff', glyph: 'H', cooldown: 9, maxLevel: 5,
    action: { type: 'area', at: 'aim', radius: 120, damage: 0.3, tags: ['curse', 'area'], status: [{ id: 'vulnerable', duration: 5 }, { id: 'weak', duration: 5 }], visual: 'hex' },
    perLevel: { damage: 0.2, radius: 10, duration: 0.5 },
    heroWeight: { hexer: 3, mystic: 1.5, warden: 1.2 },
    aspects: [
      { id: 'hex_plague', action: { type: 'area', at: 'aim', radius: 130, damage: 0.3, tags: ['curse', 'blight', 'area'], status: [{ id: 'vulnerable', duration: 5 }, { id: 'blight', duration: 6, stacks: 4 }], visual: 'hex_plague' }, flags: ['blightSpreads'], color: '#7fe07a' },
      { id: 'hex_gravity', action: { type: 'area', at: 'aim', radius: 150, damage: 0.6, tags: ['curse', 'void', 'area'], status: [{ id: 'stun', duration: 0.7 }, { id: 'vulnerable', duration: 4 }], pull: 900, visual: 'hex_gravity' }, color: '#5a3cff' },
    ],
  },
  fireball: {
    id: 'fireball', kind: 'active', tags: ['fire', 'projectile', 'area'], color: '#ff7a2f', glyph: 'F', cooldown: 5, maxLevel: 5,
    action: P({ damage: 1.6, radius: 10, speed: 480, tags: ['fire', 'projectile'], visual: 'fireball', onHit: { type: 'area', at: 'target', radius: 70, damage: 1.2, tags: ['fire', 'area'], status: [{ id: 'burn', duration: 4, stacks: 2 }], visual: 'explosion_fire' } }),
    perLevel: { damage: 0.25, radius: 6 },
    heroWeight: { hexer: 2, shaman: 2.5 },
    aspects: [
      { id: 'comet', action: P({ damage: 2.4, radius: 18, speed: 300, tags: ['fire', 'projectile'], visual: 'comet', onHit: { type: 'area', at: 'target', radius: 150, damage: 2.2, tags: ['fire', 'area'], status: [{ id: 'burn', duration: 5, stacks: 5 }], visual: 'explosion_fire_big' } }), cooldownMul: 1.4, color: '#ff4d1a' },
      { id: 'ember_swarm', action: P({ count: 6, spread: 1.4, damage: 0.55, radius: 6, speed: 380, homing: 5, tags: ['fire', 'projectile'], visual: 'ember', status: [{ id: 'burn', duration: 3, stacks: 1 }] }), color: '#ffb347' },
    ],
  },
  frost_nova: {
    id: 'frost_nova', kind: 'active', tags: ['frost', 'area'], color: '#7fdcff', glyph: 'N', cooldown: 8, maxLevel: 5,
    action: { type: 'area', at: 'self', radius: 150, damage: 1.0, tags: ['frost', 'area'], status: [{ id: 'chill', duration: 4, stacks: 3 }], visual: 'nova_frost' },
    perLevel: { damage: 0.2, radius: 12 },
    heroWeight: { hexer: 2, revenant: 1.5, shaman: 2.5 },
    aspects: [
      { id: 'glacier', action: { type: 'area', at: 'self', radius: 160, damage: 0.5, duration: 4, tickRate: 2, tags: ['frost', 'area'], status: [{ id: 'chill', duration: 2, stacks: 1 }], visual: 'zone_frost' }, color: '#bfefff' },
      { id: 'shatter', action: { type: 'area', at: 'self', radius: 170, damage: 1.2, tags: ['frost', 'area'], consume: { status: 'chill', perStack: 0.6 }, visual: 'nova_shatter' }, flags: ['shatter'], color: '#e0f7ff' },
    ],
  },
  chain_lightning: {
    id: 'chain_lightning', kind: 'active', tags: ['lightning'], color: '#a8f0ff', glyph: 'L', cooldown: 4.5, maxLevel: 5,
    action: { type: 'chain', jumps: 4, range: 220, damage: 1.3, tags: ['lightning'], status: [{ id: 'shock', duration: 4, stacks: 1 }], visual: 'chain' },
    perLevel: { damage: 0.25, count: 0.5 },
    heroWeight: { hexer: 2, pilgrim: 1.5, shaman: 2.5 },
    aspects: [
      { id: 'storm', action: { type: 'area', at: 'aim', radius: 130, damage: 0.6, duration: 4, tickRate: 3, tags: ['lightning', 'area'], status: [{ id: 'shock', duration: 3 }], visual: 'zone_storm' }, cooldownMul: 1.5, color: '#7cc7ff' },
      { id: 'overload', action: { type: 'chain', jumps: 2, range: 260, damage: 2.6, tags: ['lightning'], status: [{ id: 'shock', duration: 4, stacks: 3 }], visual: 'chain_big' }, color: '#e8fbff' },
    ],
  },
  shield_bash: {
    id: 'shield_bash', kind: 'active', tags: ['blunt', 'shield', 'movement'], color: '#d9c9a3', glyph: 'B', cooldown: 7, maxLevel: 5,
    action: { type: 'dash', distance: 140, damage: 1.5, radius: 60, tags: ['blunt', 'movement'], status: [{ id: 'stun', duration: 1 }], visual: 'bash' },
    perLevel: { damage: 0.25, radius: 6 },
    heroWeight: { revenant: 3, warden: 3, leomorph: 1.5 },
    aspects: [
      { id: 'bulwark', action: { type: 'multi', actions: [{ type: 'dash', distance: 140, damage: 1.2, radius: 60, tags: ['blunt', 'movement'], status: [{ id: 'stun', duration: 0.8 }], visual: 'bash' }, { type: 'buff', target: 'self', status: [{ id: 'shield', duration: 6, power: 0.3 }, { id: 'fortify', duration: 6 }] }] }, color: '#f0e6c8' },
      { id: 'earthquake', action: { type: 'dash', distance: 160, damage: 1.0, radius: 60, tags: ['blunt', 'movement'], visual: 'bash', trail: { type: 'area', at: 'self', radius: 190, damage: 1.6, tags: ['blunt', 'area'], knockback: 260, status: [{ id: 'stun', duration: 0.6 }], visual: 'quake' } }, cooldownMul: 1.3, color: '#b39b6a' },
    ],
  },
  blood_rite: {
    id: 'blood_rite', kind: 'active', tags: ['bleed', 'curse'], color: '#ff3b5c', glyph: 'R', cooldown: 10, maxLevel: 5,
    action: { type: 'multi', actions: [{ type: 'cost', hpPct: 0.15 }, { type: 'area', at: 'self', radius: 170, damage: 1.4, tags: ['bleed', 'area'], status: [{ id: 'bleed', duration: 6, stacks: 3 }], visual: 'blood' }, { type: 'buff', target: 'self', status: [{ id: 'might', duration: 6 }] }] },
    perLevel: { damage: 0.25, radius: 10 },
    heroWeight: { revenant: 2, hexer: 1.5, leomorph: 2.5 },
    aspects: [
      { id: 'leech', action: { type: 'multi', actions: [{ type: 'cost', hpPct: 0.1 }, { type: 'area', at: 'self', radius: 170, damage: 1.2, tags: ['bleed', 'area'], status: [{ id: 'bleed', duration: 6, stacks: 3 }], visual: 'blood' }] }, mods: [{ stat: 'lifesteal', add: 0.12 }], color: '#ff6b81' },
      { id: 'frenzy', action: { type: 'multi', actions: [{ type: 'cost', hpPct: 0.2 }, { type: 'area', at: 'self', radius: 150, damage: 1.0, tags: ['bleed', 'area'], status: [{ id: 'bleed', duration: 5, stacks: 2 }], visual: 'blood' }, { type: 'buff', target: 'self', status: [{ id: 'might', duration: 8 }, { id: 'haste', duration: 8 }] }] }, mods: [{ stat: 'attackSpeed', inc: 0.15 }], color: '#ff1f3d' },
    ],
  },
  holy_ward: {
    id: 'holy_ward', kind: 'active', tags: ['holy', 'aura', 'shield', 'heal'], color: '#fff6c2', glyph: 'A', cooldown: 12, maxLevel: 5,
    action: { type: 'multi', actions: [{ type: 'buff', target: 'allies', radius: 220, status: [{ id: 'fortify', duration: 6 }, { id: 'regen', duration: 6, stacks: 2 }, { id: 'shield', duration: 6, power: 0.2 }], visual: 'ward' }, { type: 'heal', target: 'allies', radius: 220, pct: 0.12 }] },
    perLevel: { damage: 0.2, radius: 15, duration: 1 },
    heroWeight: { pilgrim: 3, bard: 2.5, warden: 1.5 },
    aspects: [
      { id: 'sanctuary', action: { type: 'area', at: 'self', radius: 200, damage: 0.4, duration: 6, tickRate: 2, tags: ['holy', 'area', 'heal'], healAllies: 0.25, visual: 'zone_holy' }, color: '#fffbe6' },
      { id: 'retribution', action: { type: 'buff', target: 'allies', radius: 220, status: [{ id: 'fortify', duration: 8 }, { id: 'might', duration: 8 }], visual: 'ward' }, mods: [{ stat: 'thorns', add: 0.3 }], color: '#ffd166' },
    ],
  },
  void_rift: {
    id: 'void_rift', kind: 'active', tags: ['void', 'area'], color: '#6a3cff', glyph: 'O', cooldown: 11, maxLevel: 5,
    action: { type: 'area', at: 'aim', radius: 110, damage: 0.7, duration: 3, tickRate: 3, pull: 300, tags: ['void', 'area'], visual: 'rift' },
    perLevel: { damage: 0.25, radius: 8, duration: 0.4 },
    heroWeight: { hexer: 2, revenant: 1.5, mystic: 2.5 },
    aspects: [
      { id: 'singularity', action: { type: 'area', at: 'aim', radius: 120, damage: 0.5, duration: 2.5, tickRate: 3, pull: 420, tags: ['void', 'area'], visual: 'rift', status: [{ id: 'vulnerable', duration: 3 }] }, hooks: [{ on: 'cast', action: { type: 'area', at: 'aim', radius: 140, damage: 2.5, tags: ['void', 'area'], visual: 'explosion_void' } }], color: '#9b6bff' },
      { id: 'maw', action: { type: 'multi', actions: [{ type: 'area', at: 'aim', radius: 100, damage: 0.5, duration: 3, tickRate: 2, pull: 250, tags: ['void', 'area'], visual: 'rift' }, { type: 'summon', unit: 'void_maw', count: 1, duration: 10, at: 'aim' }] }, color: '#3d1fa0' },
    ],
  },
  arrow_storm: {
    id: 'arrow_storm', kind: 'active', tags: ['pierce', 'area', 'projectile'], color: '#c8d6a8', glyph: 'S', cooldown: 9, maxLevel: 5,
    action: { type: 'area', at: 'aim', radius: 140, damage: 0.55, duration: 3, tickRate: 4, tags: ['pierce', 'area'], visual: 'zone_arrows' },
    perLevel: { damage: 0.25, radius: 10 },
    heroWeight: { pilgrim: 2.5, goblins: 2, bard: 1.5 },
    aspects: [
      { id: 'pinning', action: { type: 'area', at: 'aim', radius: 140, damage: 0.45, duration: 3, tickRate: 4, tags: ['pierce', 'frost', 'area'], status: [{ id: 'chill', duration: 2, stacks: 2 }], visual: 'zone_arrows_frost' }, color: '#9fd8d8' },
      { id: 'poison_rain', action: { type: 'area', at: 'aim', radius: 160, damage: 0.35, duration: 4, tickRate: 3, tags: ['pierce', 'blight', 'area'], status: [{ id: 'blight', duration: 5, stacks: 1 }], visual: 'zone_arrows_blight' }, color: '#8fce6a' },
    ],
  },
  war_cry: {
    id: 'war_cry', kind: 'active', tags: ['aura', 'curse'], color: '#ffb35c', glyph: 'C', cooldown: 14, maxLevel: 5,
    action: { type: 'multi', actions: [{ type: 'buff', target: 'allies', radius: 260, status: [{ id: 'might', duration: 7 }, { id: 'haste', duration: 7 }], visual: 'cry' }, { type: 'buff', target: 'enemies', radius: 260, status: [{ id: 'taunt', duration: 4 }] }] },
    perLevel: { damage: 0.2, radius: 20, duration: 1 },
    heroWeight: { revenant: 2, hexer: 1.5, warden: 2.5, bard: 2.5 },
    aspects: [
      { id: 'terror', action: { type: 'buff', target: 'enemies', radius: 280, status: [{ id: 'weak', duration: 7 }, { id: 'chill', duration: 5, stacks: 3 }], visual: 'terror' }, color: '#c77dff' },
      { id: 'rally', action: { type: 'multi', actions: [{ type: 'buff', target: 'allies', radius: 280, status: [{ id: 'might', duration: 8 }, { id: 'regen', duration: 8, stacks: 2 }], visual: 'cry' }, { type: 'heal', target: 'allies', radius: 280, pct: 0.2 }] }, color: '#ffd98a' },
    ],
  },

  // ---------------------------------------------------------------- AUTO (no buttons)
  orbit_blades: {
    id: 'orbit_blades', kind: 'auto', tags: ['slash', 'projectile'], color: '#cfd8e6', glyph: '◌', cooldown: 6, maxLevel: 5,
    action: P({ count: 2, pattern: 'ring', damage: 0.8, radius: 14, pierce: 99, speed: 0, range: 9999, tags: ['slash', 'projectile'], visual: 'blade', orbit: { radius: 80, duration: 5.5, angVel: 4 } }),
    perLevel: { damage: 0.2, count: 0.5 },
    heroWeight: { revenant: 2, leomorph: 2 },
    aspects: [
      { id: 'saw', action: P({ count: 6, pattern: 'ring', damage: 0.45, radius: 9, pierce: 99, speed: 0, range: 9999, tags: ['slash', 'projectile', 'bleed'], status: [{ id: 'bleed', duration: 3, stacks: 1 }], visual: 'blade_small', orbit: { radius: 70, duration: 5.5, angVel: 6 } }), color: '#e6eef8' },
      { id: 'guillotine', action: P({ count: 1, pattern: 'ring', damage: 2.6, radius: 26, pierce: 99, speed: 0, range: 9999, tags: ['slash', 'projectile'], visual: 'blade_big', orbit: { radius: 95, duration: 5.5, angVel: 3 }, knockback: 120 }), color: '#9aa7b8' },
    ],
  },
  ember_aura: {
    id: 'ember_aura', kind: 'auto', tags: ['fire', 'aura', 'area'], color: '#ff8c42', glyph: '◎', cooldown: 1.2, maxLevel: 5,
    action: { type: 'area', at: 'self', radius: 110, damage: 0.35, tags: ['fire', 'aura', 'area'], status: [{ id: 'burn', duration: 2, stacks: 1 }], visual: 'aura_fire' },
    perLevel: { damage: 0.2, radius: 10 },
    heroWeight: { hexer: 2, revenant: 1.5, shaman: 1.5 },
    aspects: [
      { id: 'inferno', action: { type: 'area', at: 'self', radius: 150, damage: 0.6, tags: ['fire', 'aura', 'area'], status: [{ id: 'burn', duration: 2, stacks: 2 }], visual: 'aura_fire_big' }, color: '#ff5c1a' },
      { id: 'cinders', action: { type: 'area', at: 'self', radius: 110, damage: 0.3, tags: ['fire', 'aura', 'area'], status: [{ id: 'burn', duration: 2, stacks: 1 }], visual: 'aura_fire' }, hooks: [{ on: 'kill', tags: ['fire'], chance: 0.5, action: { type: 'area', at: 'target', radius: 60, damage: 0.4, duration: 3, tickRate: 2, tags: ['fire', 'area'], status: [{ id: 'burn', duration: 2, stacks: 1 }], visual: 'zone_fire' } }], color: '#ffae5c' },
    ],
  },
  frost_pulse: {
    id: 'frost_pulse', kind: 'auto', tags: ['frost', 'area'], color: '#9be7ff', glyph: '❄', cooldown: 3, maxLevel: 5,
    action: { type: 'area', at: 'self', radius: 130, damage: 0.5, tags: ['frost', 'area'], status: [{ id: 'chill', duration: 3, stacks: 1 }], visual: 'pulse_frost' },
    perLevel: { damage: 0.2, radius: 10 },
    heroWeight: { hexer: 2, shaman: 1.5, warden: 1.5 },
    aspects: [
      { id: 'hail', action: P({ count: 8, pattern: 'ring', damage: 0.5, radius: 7, speed: 420, range: 320, tags: ['frost', 'projectile'], status: [{ id: 'chill', duration: 3, stacks: 1 }], visual: 'shard_frost' }), color: '#d6f4ff' },
      { id: 'permafrost', action: { type: 'area', at: 'self', radius: 180, damage: 0.35, tags: ['frost', 'area'], status: [{ id: 'chill', duration: 4, stacks: 2 }], visual: 'pulse_frost_big' }, color: '#6fc9e6' },
    ],
  },
  spirit_bolt: {
    id: 'spirit_bolt', kind: 'auto', tags: ['void', 'projectile'], color: '#b48cff', glyph: '✦', cooldown: 1.6, maxLevel: 5,
    action: P({ pattern: 'nearest', damage: 0.9, radius: 8, speed: 460, range: 640, homing: 6, tags: ['void', 'projectile'], visual: 'bolt_void' }),
    perLevel: { damage: 0.2, count: 0.5 },
    heroWeight: { hexer: 2, pilgrim: 1.5, mystic: 1.5 },
    aspects: [
      { id: 'twin_bolt', action: P({ count: 2, spread: 0.5, pattern: 'nearest', damage: 0.7, radius: 8, speed: 460, range: 640, homing: 6, tags: ['void', 'projectile'], visual: 'bolt_void' }), color: '#cbb2ff' },
      { id: 'piercer', action: P({ pattern: 'nearest', damage: 1.5, radius: 10, speed: 700, range: 760, pierce: 3, tags: ['void', 'pierce', 'projectile'], visual: 'bolt_void_big' }), color: '#7a4cff' },
    ],
  },
  bone_shards: {
    id: 'bone_shards', kind: 'auto', tags: ['pierce', 'projectile'], color: '#efe6d2', glyph: '✧', cooldown: 2.4, maxLevel: 5,
    action: P({ count: 6, pattern: 'ring', damage: 0.55, radius: 6, speed: 500, range: 300, pierce: 1, tags: ['pierce', 'projectile'], visual: 'shard_bone' }),
    perLevel: { damage: 0.2, count: 1 },
    heroWeight: { revenant: 2, pilgrim: 1.5, goblins: 1.5 },
    aspects: [
      { id: 'spiral', action: P({ count: 10, pattern: 'spiral', damage: 0.5, radius: 6, speed: 380, range: 420, pierce: 2, tags: ['pierce', 'projectile'], visual: 'shard_bone' }), color: '#fff9ea' },
      { id: 'shrapnel', action: P({ count: 5, pattern: 'ring', damage: 0.5, radius: 6, speed: 500, range: 300, tags: ['pierce', 'projectile', 'area'], visual: 'shard_bone', onHit: { type: 'area', at: 'target', radius: 45, damage: 0.5, tags: ['pierce', 'area'], visual: 'burst_bone' } }), color: '#d8ccb0' },
    ],
  },
  blight_cloud: {
    id: 'blight_cloud', kind: 'auto', tags: ['blight', 'area', 'curse'], color: '#8fd66a', glyph: '☁', cooldown: 4, maxLevel: 5,
    action: { type: 'area', at: 'self', radius: 120, damage: 0.2, duration: 3, tickRate: 2, tags: ['blight', 'area'], status: [{ id: 'blight', duration: 5, stacks: 1 }], visual: 'zone_blight' },
    perLevel: { damage: 0.2, radius: 10 },
    heroWeight: { hexer: 2.5, mystic: 2.5, druid: 1.5 },
    aspects: [
      { id: 'spores', action: { type: 'multi', actions: [{ type: 'area', at: 'self', radius: 120, damage: 0.2, duration: 3, tickRate: 2, tags: ['blight', 'area'], status: [{ id: 'blight', duration: 5, stacks: 1 }], visual: 'zone_blight' }, { type: 'summon', unit: 'spore_turret', count: 1, duration: 8, at: 'self' }] }, color: '#b6e69a' },
      { id: 'miasma', action: { type: 'area', at: 'self', radius: 160, damage: 0.25, duration: 3, tickRate: 2, tags: ['blight', 'curse', 'area'], status: [{ id: 'blight', duration: 5, stacks: 1 }, { id: 'weak', duration: 3 }], visual: 'zone_miasma' }, color: '#5d9f3f' },
    ],
  },
  guardian_totem: {
    id: 'guardian_totem', kind: 'auto', tags: ['summon'], color: '#c9a86a', glyph: '♜', cooldown: 10, maxLevel: 5,
    action: { type: 'summon', unit: 'totem', count: 1, duration: 9, at: 'self' },
    perLevel: { damage: 0.25, duration: 1 },
    heroWeight: { revenant: 2, pilgrim: 1.5, goblins: 2.5, shaman: 2 },
    aspects: [
      { id: 'healing_totem', action: { type: 'summon', unit: 'healing_totem', count: 1, duration: 10, at: 'self' }, color: '#ffe9b0' },
      { id: 'thunder_totem', action: { type: 'summon', unit: 'thunder_totem', count: 1, duration: 9, at: 'self' }, color: '#a9dcff' },
    ],
  },
  mirror_shards: {
    id: 'mirror_shards', kind: 'auto', tags: ['shield', 'projectile'], color: '#dfe9ff', glyph: '◈', cooldown: 1, maxLevel: 5,
    hooks: [{ on: 'damaged', cooldown: 0.8, action: P({ count: 4, pattern: 'nearest', spread: 0.8, damage: 0.8, radius: 7, speed: 560, range: 420, tags: ['pierce', 'projectile'], visual: 'shard_mirror' }) }],
    perLevel: { damage: 0.25, count: 0.5 },
    heroWeight: { revenant: 2.5, warden: 2 },
    aspects: [
      { id: 'mirror_wall', hooks: [{ on: 'damaged', cooldown: 4, action: { type: 'buff', target: 'self', status: [{ id: 'shield', duration: 3, power: 0.15 }] } }, { on: 'damaged', cooldown: 0.8, action: P({ count: 3, pattern: 'nearest', spread: 0.6, damage: 0.7, radius: 7, speed: 560, range: 420, tags: ['pierce', 'projectile'], visual: 'shard_mirror' }) }], color: '#f4f8ff' },
      { id: 'reflection', hooks: [{ on: 'damaged', cooldown: 0.6, action: P({ count: 6, pattern: 'ring', damage: 0.6, radius: 7, speed: 520, range: 380, tags: ['pierce', 'projectile'], visual: 'shard_mirror' }) }], mods: [{ stat: 'thorns', add: 0.2 }], color: '#b7c9ea' },
    ],
  },

  // ---------------------------------------------------------------- SIGNATURES of the unlockable heroes
  bulwark_oath: {
    id: 'bulwark_oath', kind: 'active', tags: ['shield', 'curse', 'aura'], color: '#9fd3ff', glyph: 'O', cooldown: 12, maxLevel: 5,
    action: {
      type: 'multi',
      actions: [
        { type: 'buff', target: 'self', status: [{ id: 'fortify', duration: 6 }, { id: 'shield', duration: 5, power: 0.25 }], visual: 'ward' },
        { type: 'buff', target: 'enemies', at: 'self', radius: 190, status: [{ id: 'weak', duration: 5 }, { id: 'taunt', duration: 3 }] },
      ],
    },
    perLevel: { duration: 1, radius: 10 },
    heroWeight: { warden: 3 },
    aspects: [
      { id: 'oath_iron', action: { type: 'multi', actions: [{ type: 'buff', target: 'self', status: [{ id: 'fortify', duration: 8 }, { id: 'shield', duration: 6, power: 0.4 }], visual: 'ward' }, { type: 'buff', target: 'enemies', at: 'self', radius: 190, status: [{ id: 'weak', duration: 5 }, { id: 'taunt', duration: 4 }] }] }, mods: [{ stat: 'thorns', add: 0.3 }], color: '#dfe9ff' },
      { id: 'oath_dread', action: { type: 'multi', actions: [{ type: 'buff', target: 'self', status: [{ id: 'fortify', duration: 5 }], visual: 'terror' }, { type: 'buff', target: 'enemies', at: 'self', radius: 240, status: [{ id: 'weak', duration: 6 }, { id: 'vulnerable', duration: 5 }, { id: 'taunt', duration: 2 }] }] }, color: '#8a6aff' },
    ],
  },
  spirit_pact: {
    id: 'spirit_pact', kind: 'active', tags: ['summon', 'void', 'curse'], color: '#ff6ad5', glyph: 'P', cooldown: 13, maxLevel: 5,
    action: { type: 'summon', unit: 'imp', count: 2, duration: 10, at: 'self' },
    perLevel: { damage: 0.2, count: 0.5, duration: 1 },
    heroWeight: { mystic: 3 },
    aspects: [
      { id: 'pact_swarm', action: { type: 'summon', unit: 'imp_lesser', count: 5, duration: 8, at: 'self' }, color: '#ff9ae8' },
      { id: 'pact_fiend', action: { type: 'summon', unit: 'fiend', count: 1, duration: 14, at: 'aim' }, color: '#c23fa8' },
    ],
  },
  thornroot: {
    id: 'thornroot', kind: 'active', tags: ['summon', 'blight', 'area'], color: '#8fce6a', glyph: 'T', cooldown: 11, maxLevel: 5,
    action: { type: 'summon', unit: 'thornroot', count: 1, duration: 12, at: 'aim' },
    perLevel: { damage: 0.2, duration: 1.5 },
    heroWeight: { mystic: 3, druid: 2 },
    aspects: [
      { id: 'root_grove', action: { type: 'summon', unit: 'thornroot', count: 3, duration: 10, at: 'aim' }, cooldownMul: 1.3, color: '#b6e69a' },
      { id: 'root_bloom', action: { type: 'summon', unit: 'bloomroot', count: 1, duration: 14, at: 'aim' }, color: '#ffd7f0' },
    ],
  },
  tiger_form: {
    id: 'tiger_form', kind: 'active', tags: ['movement', 'slash', 'heal'], color: '#ffb347', glyph: 'F', cooldown: 16, maxLevel: 5,
    action: { type: 'multi', actions: [{ type: 'buff', target: 'self', status: [{ id: 'tiger', duration: 8 }], visual: 'haste' }, { type: 'heal', target: 'self', pct: 0.12 }] },
    perLevel: { duration: 1, damage: 0.1 },
    heroWeight: { leomorph: 3 },
    aspects: [
      { id: 'form_bear', action: { type: 'multi', actions: [{ type: 'buff', target: 'self', status: [{ id: 'bear', duration: 10 }, { id: 'regen', duration: 10, stacks: 2 }], visual: 'ward' }, { type: 'heal', target: 'self', pct: 0.2 }] }, color: '#b07a4a' },
      { id: 'form_stalker', action: { type: 'multi', actions: [{ type: 'buff', target: 'self', status: [{ id: 'tiger', duration: 9 }, { id: 'haste', duration: 4 }], visual: 'haste' }, { type: 'dash', distance: 150, damage: 1.4, radius: 60, tags: ['slash', 'bleed'], status: [{ id: 'bleed', duration: 4, stacks: 2 }], visual: 'dash' }] }, cooldownMul: 0.85, color: '#ffe0a0' },
    ],
  },
  kindred_beast: {
    id: 'kindred_beast', kind: 'active', tags: ['summon', 'heal'], color: '#8fe38a', glyph: 'K', cooldown: 16, maxLevel: 5,
    action: { type: 'summon', unit: 'kindred_bear', count: 1, duration: 20, at: 'self', unique: true },
    perLevel: { damage: 0.25, duration: 2 },
    heroWeight: { druid: 3 },
    // the whole tree lives in aspects.ts: dragon / three bears / armoured bear, each with three evolutions
  },
  walking_tree: {
    id: 'walking_tree', kind: 'active', tags: ['summon', 'blunt'], color: '#6fae5a', glyph: 'W', cooldown: 18, maxLevel: 5,
    action: { type: 'summon', unit: 'treant', count: 1, duration: 20, at: 'self', unique: true },
    perLevel: { damage: 0.25, duration: 2 },
    heroWeight: { druid: 3 },
  },
  vine: {
    id: 'vine', kind: 'active', tags: ['summon', 'frost', 'area'], color: '#9fd47a', glyph: 'V', cooldown: 11, maxLevel: 5,
    action: { type: 'summon', unit: 'vine', count: 1, duration: 12, at: 'aim' },
    perLevel: { damage: 0.2, duration: 1.5 },
    heroWeight: { druid: 3 },
  },
  flytrap: {
    id: 'flytrap', kind: 'active', tags: ['summon', 'blight', 'area'], color: '#c96fa0', glyph: 'M', cooldown: 12, maxLevel: 5,
    action: { type: 'summon', unit: 'flytrap', count: 1, duration: 14, at: 'aim' },
    perLevel: { damage: 0.25, duration: 1.5 },
    heroWeight: { druid: 3 },
  },
  attunement: {
    id: 'attunement', kind: 'active', tags: ['fire', 'frost', 'lightning'], color: '#ff8c42', glyph: 'A', cooldown: 2.5, maxLevel: 5,
    action: { type: 'stance', statuses: ['attune_fire', 'attune_frost', 'attune_storm'], duration: 40, visual: 'shift' },
    perLevel: { damage: 0.15 },
    heroWeight: { shaman: 3 },
    aspects: [
      { id: 'attune_fury', action: { type: 'multi', actions: [{ type: 'stance', statuses: ['attune_fire', 'attune_frost', 'attune_storm'], duration: 40, visual: 'shift' }, { type: 'area', at: 'self', radius: 130, damage: 0.9, tags: ['fire', 'frost', 'lightning', 'area'], visual: 'nova_shift' }] }, cooldownMul: 1.6, color: '#ffd27a' },
      { id: 'attune_focus', mods: [{ stat: 'damage', inc: 0.15 }, { stat: 'projSpeed', inc: 0.2 }], color: '#ffefc0' },
    ],
  },
  turret: {
    id: 'turret', kind: 'active', tags: ['summon', 'void', 'projectile'], color: '#a7ff5c', glyph: 'U', cooldown: 9, maxLevel: 5,
    action: { type: 'summon', unit: 'gob_turret', count: 1, duration: 14, at: 'aim' },
    perLevel: { damage: 0.2, duration: 1.5 },
    heroWeight: { goblins: 3 },
    aspects: [
      { id: 'turret_twin', action: { type: 'summon', unit: 'gob_turret', count: 2, duration: 11, at: 'aim' }, color: '#d2ff9a' },
      { id: 'turret_cannon', action: { type: 'summon', unit: 'gob_cannon', count: 1, duration: 14, at: 'aim' }, cooldownMul: 1.25, color: '#7fd63a' },
    ],
  },
  snare_trap: {
    id: 'snare_trap', kind: 'active', tags: ['area', 'void'], color: '#c8ff8a', glyph: 'X', cooldown: 8, maxLevel: 5,
    action: { type: 'area', at: 'aim', radius: 95, damage: 0.5, duration: 6, tickRate: 1, tags: ['void', 'area'], status: [{ id: 'chill', duration: 2, stacks: 3 }, { id: 'vulnerable', duration: 2 }], visual: 'zone_trap' },
    perLevel: { damage: 0.25, radius: 8 },
    heroWeight: { goblins: 3 },
    aspects: [
      { id: 'trap_shock', action: { type: 'area', at: 'aim', radius: 100, damage: 0.7, duration: 6, tickRate: 1, tags: ['lightning', 'area'], status: [{ id: 'shock', duration: 3, stacks: 1 }, { id: 'stun', duration: 0.5 }], visual: 'zone_storm' }, color: '#c9f3ff' },
      { id: 'trap_maw', action: { type: 'area', at: 'aim', radius: 110, damage: 0.6, duration: 5, tickRate: 2, pull: 240, tags: ['void', 'area'], visual: 'rift' }, color: '#6a3cff' },
    ],
  },
  rallying_chord: {
    id: 'rallying_chord', kind: 'active', tags: ['aura', 'heal', 'holy'], color: '#ffd166', glyph: 'R', cooldown: 10, maxLevel: 5,
    action: { type: 'buff', target: 'allies', radius: 260, status: [{ id: 'inspire', duration: 6 }], cleanse: true, visual: 'cry' },
    perLevel: { duration: 1, radius: 15 },
    heroWeight: { bard: 3 },
    aspects: [
      { id: 'chord_march', action: { type: 'buff', target: 'allies', radius: 260, status: [{ id: 'inspire', duration: 6 }, { id: 'haste', duration: 4 }], cleanse: true, visual: 'cry' }, color: '#fff2b3' },
      { id: 'chord_hymn', action: { type: 'multi', actions: [{ type: 'buff', target: 'allies', radius: 280, status: [{ id: 'inspire', duration: 7 }], cleanse: true, visual: 'heal' }, { type: 'heal', target: 'allies', radius: 280, power: 0.6 }] }, cooldownMul: 1.2, color: '#ffe38a' },
    ],
  },

  // ---------------------------------------------------------------- TRAITS
  iron_skin: { id: 'iron_skin', kind: 'trait', tags: ['shield'], color: '#9aa5b1', glyph: '▣', maxLevel: 3, mods: [{ stat: 'armor', add: 15 }], modsPerLevel: [{ stat: 'armor', add: 10 }], heroWeight: { revenant: 2, warden: 3 } },
  glass_heart: { id: 'glass_heart', kind: 'trait', tags: ['crit'], color: '#ff9ecf', glyph: '◇', maxLevel: 3, mods: [{ stat: 'damage', inc: 0.25 }, { stat: 'maxHp', inc: -0.15 }], modsPerLevel: [{ stat: 'damage', inc: 0.15 }, { stat: 'maxHp', inc: -0.05 }], heroWeight: { pilgrim: 2, hexer: 1.5, shaman: 1.5 } },
  swift: { id: 'swift', kind: 'trait', tags: ['movement'], color: '#b8ffb0', glyph: '»', maxLevel: 3, mods: [{ stat: 'moveSpeed', inc: 0.12 }, { stat: 'dashCooldown', inc: -0.15 }], modsPerLevel: [{ stat: 'moveSpeed', inc: 0.06 }, { stat: 'dashCooldown', inc: -0.1 }] },
  bloodlust: { id: 'bloodlust', kind: 'trait', tags: ['bleed', 'movement'], color: '#ff6a6a', glyph: '♥', maxLevel: 3, mods: [{ stat: 'damage', inc: 0.05 }], modsPerLevel: [{ stat: 'damage', inc: 0.05 }], hooks: [{ on: 'kill', cooldown: 0.5, action: { type: 'buff', target: 'self', status: [{ id: 'haste', duration: 2 }] } }], heroWeight: { revenant: 1.5, leomorph: 2.5 } },
  pyromancer: { id: 'pyromancer', kind: 'trait', tags: ['fire'], color: '#ff9a3c', glyph: '🜂', maxLevel: 3, mods: [{ stat: 'statusPower', inc: 0.15 }], modsPerLevel: [{ stat: 'statusPower', inc: 0.1 }], hooks: [{ on: 'hit', tags: ['fire'], chance: 0.35, action: { type: 'buff', target: 'enemies', radius: 1, status: [{ id: 'burn', duration: 3, stacks: 1 }] } }], synergy: ['fire'], heroWeight: { hexer: 2, shaman: 2 } },
  cryomancer: { id: 'cryomancer', kind: 'trait', tags: ['frost'], color: '#a3e4ff', glyph: '🜄', maxLevel: 3, mods: [{ stat: 'statusPower', inc: 0.15 }], modsPerLevel: [{ stat: 'statusPower', inc: 0.1 }], flags: ['shatter'], hooks: [{ on: 'hit', tags: ['frost'], chance: 0.4, action: { type: 'buff', target: 'enemies', radius: 1, status: [{ id: 'chill', duration: 3, stacks: 1 }] } }], synergy: ['frost'], heroWeight: { hexer: 2, shaman: 2 } },
  stormcaller: { id: 'stormcaller', kind: 'trait', tags: ['lightning'], color: '#c9f3ff', glyph: '🜁', maxLevel: 3, mods: [{ stat: 'damage', inc: 0.08 }], modsPerLevel: [{ stat: 'damage', inc: 0.06 }], hooks: [{ on: 'kill', cooldown: 1, chance: 0.6, action: { type: 'chain', jumps: 2, range: 200, damage: 0.8, tags: ['lightning'], status: [{ id: 'shock', duration: 3 }], visual: 'chain' } }], synergy: ['lightning'], heroWeight: { hexer: 1.5, pilgrim: 1.5, shaman: 2 } },
  plague_doctor: { id: 'plague_doctor', kind: 'trait', tags: ['blight', 'curse'], color: '#9ee37d', glyph: '☣', maxLevel: 3, mods: [{ stat: 'statusPower', inc: 0.2 }], modsPerLevel: [{ stat: 'statusPower', inc: 0.15 }], flags: ['blightSpreads'], synergy: ['blight'], heroWeight: { hexer: 2, mystic: 2.5, druid: 1.5 } },
  thorns: { id: 'thorns', kind: 'trait', tags: ['shield'], color: '#d3a86b', glyph: '✱', maxLevel: 3, mods: [{ stat: 'thorns', add: 0.25 }], modsPerLevel: [{ stat: 'thorns', add: 0.2 }], heroWeight: { revenant: 2, warden: 2.5 } },
  shepherd: { id: 'shepherd', kind: 'trait', tags: ['summon'], color: '#c8b8ff', glyph: '♆', maxLevel: 3, mods: [{ stat: 'summonCount', add: 1 }, { stat: 'summonPower', inc: 0.2 }], modsPerLevel: [{ stat: 'summonPower', inc: 0.2 }], synergy: ['summon'], heroWeight: { revenant: 2, druid: 3, mystic: 2 } },
  zealot: { id: 'zealot', kind: 'trait', tags: ['holy', 'heal'], color: '#fff0a8', glyph: '✚', maxLevel: 3, mods: [{ stat: 'healPower', inc: 0.3 }, { stat: 'damage', inc: 0.05 }], modsPerLevel: [{ stat: 'healPower', inc: 0.15 }, { stat: 'damage', inc: 0.05 }], flags: ['holySmite'], synergy: ['holy'], heroWeight: { pilgrim: 2.5, bard: 2 } },
  marksman: { id: 'marksman', kind: 'trait', tags: ['pierce', 'crit', 'projectile'], color: '#e0e6b8', glyph: '➶', maxLevel: 3, mods: [{ stat: 'projCount', add: 1 }, { stat: 'critChance', add: 0.08 }], modsPerLevel: [{ stat: 'critChance', add: 0.06 }, { stat: 'projSpeed', inc: 0.1 }], heroWeight: { pilgrim: 2.5, goblins: 1.5, bard: 1.5 } },
  vampiric: { id: 'vampiric', kind: 'trait', tags: ['bleed', 'curse'], color: '#c0304f', glyph: '☽', maxLevel: 3, mods: [{ stat: 'lifesteal', add: 0.05 }], modsPerLevel: [{ stat: 'lifesteal', add: 0.03 }], heroWeight: { revenant: 1.5, hexer: 1.5, leomorph: 2 } },
  alchemist: { id: 'alchemist', kind: 'trait', tags: ['heal'], color: '#7be0c8', glyph: '⚗', maxLevel: 2, mods: [{ stat: 'pickupRadius', inc: 0.5 }, { stat: 'luck', add: 1 }], modsPerLevel: [{ stat: 'luck', add: 1 }], flags: ['consumablesDouble'], heroWeight: { goblins: 2 } },
  momentum: { id: 'momentum', kind: 'trait', tags: ['movement'], color: '#a6f0ff', glyph: '⇶', maxLevel: 3, mods: [{ stat: 'dashCooldown', inc: -0.1 }], modsPerLevel: [{ stat: 'dashCooldown', inc: -0.1 }], hooks: [{ on: 'dash', action: { type: 'buff', target: 'self', status: [{ id: 'might', duration: 3 }] } }], synergy: ['movement'], heroWeight: { leomorph: 1.5, bard: 1.2 } },
  warlock: { id: 'warlock', kind: 'trait', tags: ['curse'], color: '#b070ff', glyph: '☿', maxLevel: 3, mods: [{ stat: 'duration', inc: 0.25 }], modsPerLevel: [{ stat: 'duration', inc: 0.15 }], flags: ['curseAmplify'], synergy: ['curse'], heroWeight: { hexer: 2.5, mystic: 2, warden: 1.5 } },
  bulwark_trait: { id: 'bulwark_trait', kind: 'trait', tags: ['shield'], color: '#e8dcc0', glyph: '⛨', maxLevel: 3, mods: [{ stat: 'maxHp', inc: 0.12 }], modsPerLevel: [{ stat: 'maxHp', inc: 0.08 }], hooks: [{ on: 'damaged', chance: 0.3, cooldown: 3, action: { type: 'buff', target: 'self', status: [{ id: 'fortify', duration: 3 }] } }], heroWeight: { revenant: 2, warden: 2, leomorph: 2 } },
  exploder: { id: 'exploder', kind: 'trait', tags: ['fire', 'area'], color: '#ffb86b', glyph: '✸', maxLevel: 3, hooks: [{ on: 'kill', chance: 0.25, action: { type: 'area', at: 'target', radius: 70, damage: 1.0, tags: ['fire', 'area'], visual: 'explosion_fire' } }], modsPerLevel: [{ stat: 'area', inc: 0.1 }], synergy: ['fire', 'area'], heroWeight: { goblins: 2, shaman: 1.2 } },
  executioner: { id: 'executioner', kind: 'trait', tags: ['crit', 'slash'], color: '#ffd1d1', glyph: '⚔', maxLevel: 3, mods: [{ stat: 'critMult', add: 0.5 }], modsPerLevel: [{ stat: 'critMult', add: 0.3 }], hooks: [{ on: 'crit', chance: 0.3, cooldown: 0.3, action: { type: 'buff', target: 'enemies', radius: 1, status: [{ id: 'bleed', duration: 4, stacks: 2 }] } }], heroWeight: { pilgrim: 1.5, revenant: 1.5, leomorph: 1.5 } },
};

// ------------------------------------------------------------------ evolution trees

/** Extra ring-one nodes and ring-two children authored in `aspects.ts` are grafted here once. */
import { EXTRA_ASPECTS, ASPECT_CHILDREN } from './aspects';
for (const [sid, list] of Object.entries(EXTRA_ASPECTS)) {
  const def = SKILLS[sid];
  if (!def) continue;
  def.aspects = [...(def.aspects ?? []), ...list];
}
for (const def of Object.values(SKILLS)) {
  for (const a of def.aspects ?? []) {
    const kids = ASPECT_CHILDREN[a.id];
    if (kids) a.children = [...(a.children ?? []), ...kids];
  }
}

export const ACTIVE_IDS = Object.values(SKILLS).filter((s) => s.kind === 'active').map((s) => s.id);
export const AUTO_IDS = Object.values(SKILLS).filter((s) => s.kind === 'auto').map((s) => s.id);
export const TRAIT_IDS = Object.values(SKILLS).filter((s) => s.kind === 'trait').map((s) => s.id);

/** Finds an aspect anywhere in the skill's tree. */
export function aspectOf(skillId: string, aspectId: string | undefined): AspectDef | undefined {
  if (!aspectId) return undefined;
  const walk = (list: AspectDef[] | undefined): AspectDef | undefined => {
    if (!list) return undefined;
    for (const a of list) {
      if (a.id === aspectId) return a;
      const c = walk(a.children);
      if (c) return c;
    }
    return undefined;
  };
  return walk(SKILLS[skillId]?.aspects);
}

/** Validates an aspect path (ring one, then one of its children) and returns the nodes; invalid tails are dropped. */
export function aspectPath(skillId: string, ids: string[] | undefined): AspectDef[] {
  const out: AspectDef[] = [];
  if (!ids || !ids.length) return out;
  let list = SKILLS[skillId]?.aspects;
  for (const id of ids) {
    const node = list?.find((a) => a.id === id);
    if (!node) break;
    out.push(node);
    list = node.children;
  }
  return out;
}

/** Ring-one options of a skill, or ring-two options under the chosen ring-one node. */
export function aspectOptions(skillId: string, chosen: string[]): AspectDef[] {
  const path = aspectPath(skillId, chosen);
  if (!chosen.length) return SKILLS[skillId]?.aspects ?? [];
  if (path.length === 1) return path[0].children ?? [];
  return [];
}

export interface ResolvedSkill {
  action?: Action;
  cooldownMul: number;
  /** perks of every aspect on the path, in order */
  perks: Perk[];
  /** colour of the deepest aspect, for icons */
  color: string;
}

/** Composes the skill's action through its aspect path: ring one, then ring two on top of it. */
export function resolveSkill(def: SkillDef, aspects: string[] | undefined): ResolvedSkill {
  let action = def.action;
  let cooldownMul = 1;
  const perks: Perk[] = [];
  let color = def.color;
  for (const node of aspectPath(def.id, aspects)) {
    if (node.action) action = node.action;
    else if (node.patch && action) action = patchAction(action, node.patch);
    if (node.cooldownMul) cooldownMul *= node.cooldownMul;
    if (node.mods || node.hooks || node.flags) perks.push({ mods: node.mods, hooks: node.hooks, flags: node.flags });
    if (node.color) color = node.color;
  }
  return { action, cooldownMul, perks, color };
}

// ------------------------------------------------------------------ level numbers

/** The numbers a skill actually has at a given rank, after its aspect path. */
export interface SkillNumbers {
  /** damage multiplier the level adds (1.0 at level 1) */
  damage: number;
  /** seconds between casts */
  cooldown: number;
  /** projectiles, summoned units or chain jumps */
  count?: number;
  /** area or blast radius in px */
  radius?: number;
  /** seconds a summon or a zone lasts */
  duration?: number;
}

function leaves(a: Action | undefined, out: Action[] = []): Action[] {
  if (!a) return out;
  if (a.type === 'multi') for (const x of a.actions) leaves(x, out);
  else out.push(a);
  return out;
}

function statusDuration(a: Action): number | undefined {
  const list = a.type === 'buff' ? a.status : undefined;
  if (a.type === 'stance') return a.duration;
  if (!list || !list.length) return undefined;
  return Math.max(...list.map((s) => s.duration));
}

/** The pieces a level actually grows: counts, the area it covers and how long it lasts. */
function partNumbers(a: Action): { count?: number; radius?: number; duration?: number } {
  switch (a.type) {
    case 'projectile':
      return { count: a.count, radius: a.onHit && a.onHit.type === 'area' ? a.onHit.radius : undefined, duration: a.orbit?.duration };
    case 'summon':
      return { count: a.count, duration: a.duration };
    case 'chain':
      return { count: a.jumps };
    case 'area':
      return { radius: a.radius, duration: a.duration };
    case 'buff':
    case 'stance':
      return { radius: a.type === 'buff' ? a.radius : undefined, duration: statusDuration(a) };
    case 'heal':
      return { radius: a.radius };
    case 'dash':
      return { radius: a.radius };
    default:
      return {};
  }
}

export function skillNumbers(def: SkillDef, aspects: string[] | undefined, level: number): SkillNumbers {
  const res = resolveSkill(def, aspects);
  const lv = Math.max(0, Math.max(1, level) - 1);
  const pl = def.perLevel ?? {};
  const out: SkillNumbers = {
    damage: 1 + (pl.damage ?? 0) * lv,
    cooldown: Math.round((def.cooldown ?? 0) * res.cooldownMul * (1 + (pl.cooldown ?? 0) * lv) * 10) / 10,
  };
  const parts = leaves(res.action).map(partNumbers);
  const best = (pick: (x: { count?: number; radius?: number; duration?: number }) => number | undefined): number | undefined => {
    const vals = parts.map(pick).filter((v): v is number => typeof v === 'number' && v > 0);
    return vals.length ? Math.max(...vals) : undefined;
  };
  const count = best((x) => x.count);
  const radius = best((x) => x.radius);
  const duration = best((x) => x.duration);
  if (count !== undefined) out.count = count + Math.floor((pl.count ?? 0) * lv);
  if (radius !== undefined) out.radius = Math.round(radius + (pl.radius ?? 0) * lv);
  if (duration !== undefined) out.duration = Math.round((duration + (pl.duration ?? 0) * lv) * 10) / 10;
  return out;
}
